<script setup>
import { ref, computed, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useEmitter } from 'dashboard/composables/emitter';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  isAConversationRoute,
  isAInboxViewRoute,
  isNotificationRoute,
} from 'dashboard/helper/routeHelpers';
import { isFunilChatFrame } from 'dashboard/helper/aceleresDisconnectedInstanceAlert';
import { useEventListener } from '@vueuse/core';

import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const route = useRoute();

const RECONNECTED_BANNER_TIMEOUT = 2000;
const WEBSOCKET_NOTIFICATION_DELAY = 3000;
const isContactsPage = computed(() =>
  String(route.name || '').startsWith('contacts_')
);

const isFunilChat = isFunilChatFrame();
const showNotification = ref(!isFunilChat && !navigator.onLine);
const isDisconnected = ref(false);
const isReconnecting = ref(false);
const isReconnected = ref(false);
let reconnectTimeout = null;
let websocketDisconnectTimeout = null;

const bannerText = computed(() => {
  if (isReconnecting.value) return t('NETWORK.NOTIFICATION.RECONNECTING');
  if (isReconnected.value) return t('NETWORK.NOTIFICATION.RECONNECT_SUCCESS');
  return t('NETWORK.NOTIFICATION.OFFLINE');
});

const iconName = computed(() => (isReconnected.value ? 'wifi' : 'wifi-off'));
const canRefresh = computed(
  () => !isReconnecting.value && !isReconnected.value
);

const refreshPage = () => {
  window.location.reload();
};

const closeNotification = () => {
  showNotification.value = false;
  isDisconnected.value = false;
  isReconnecting.value = false;
  isReconnected.value = false;
  clearTimeout(reconnectTimeout);
  clearTimeout(websocketDisconnectTimeout);
};

const isInAnyOfTheRoutes = routeName => {
  return (
    isAConversationRoute(routeName, true) ||
    isAInboxViewRoute(routeName, true) ||
    isNotificationRoute(routeName, true)
  );
};

const updateWebsocketStatus = () => {
  if (isFunilChat) return;
  isDisconnected.value = true;
  // Oscilações curtas do ActionCable são recuperadas automaticamente. Exibir
  // o banner em cada uma delas fazia o painel parecer recarregar, embora a
  // conversa continuasse disponível. Só sinalizamos ao atendente uma queda
  // que realmente persiste.
  clearTimeout(websocketDisconnectTimeout);
  websocketDisconnectTimeout = setTimeout(() => {
    if (isDisconnected.value) showNotification.value = true;
  }, WEBSOCKET_NOTIFICATION_DELAY);
};

const handleReconnectionCompleted = () => {
  if (isFunilChat) return;
  const wasVisible = showNotification.value;
  clearTimeout(websocketDisconnectTimeout);
  isDisconnected.value = false;
  isReconnecting.value = false;
  isReconnected.value = wasVisible;
  showNotification.value = wasVisible;
  if (!wasVisible) return;
  reconnectTimeout = setTimeout(closeNotification, RECONNECTED_BANNER_TIMEOUT);
};

const handleReconnecting = () => {
  if (isFunilChat) return;
  clearTimeout(websocketDisconnectTimeout);
  if (isInAnyOfTheRoutes(route.name)) {
    // Mantém o feedback somente se a indisponibilidade já foi perceptível.
    // Para reconexões menores que o limiar, o usuário não vê um flash.
    if (showNotification.value) {
      isReconnecting.value = true;
      isReconnected.value = false;
    }
  } else {
    handleReconnectionCompleted();
  }
};

const updateOnlineStatus = event => {
  // Case: Websocket is not disconnected
  // If the app goes offline, show the notification
  // If the app goes online, close the notification

  // Case: Websocket is disconnected
  // If the app goes offline, show the notification
  // If the app goes online but the websocket is disconnected, don't close the notification
  // If the app goes online and the websocket is not disconnected, close the notification

  if (isFunilChat) return;

  if (event.type === 'offline') {
    showNotification.value = true;
  } else if (event.type === 'online' && !isDisconnected.value) {
    handleReconnectionCompleted();
  }
};

useEventListener('online', updateOnlineStatus);
useEventListener('offline', updateOnlineStatus);
useEmitter(BUS_EVENTS.WEBSOCKET_DISCONNECT, updateWebsocketStatus);
useEmitter(
  BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED,
  handleReconnectionCompleted
);
useEmitter(BUS_EVENTS.WEBSOCKET_RECONNECT, handleReconnecting);

onBeforeUnmount(() => {
  clearTimeout(reconnectTimeout);
  clearTimeout(websocketDisconnectTimeout);
});
</script>

<template>
  <transition name="network-notification-fade" tag="div">
    <div
      v-show="showNotification"
      class="fixed z-50 top-2 right-2 group"
      :class="{ 'max-md:!right-16': isContactsPage }"
    >
      <div
        class="relative flex items-center justify-between w-full px-2 py-1 bg-n-amber-4 dark:bg-n-amber-8 rounded-lg shadow-lg"
      >
        <fluent-icon :icon="iconName" class="text-n-amber-12" size="18" />
        <span class="px-2 text-xs font-medium tracking-wide text-n-amber-12">
          {{ bannerText }}
        </span>
        <Button
          v-if="canRefresh"
          ghost
          sm
          amber
          icon="i-lucide-refresh-ccw"
          :title="$t('NETWORK.BUTTON.REFRESH')"
          class="!text-n-amber-12 dark:!text-n-amber-9"
          @click="refreshPage"
        />

        <Button
          ghost
          sm
          amber
          icon="i-lucide-x"
          class="!text-n-amber-12 dark:!text-n-amber-9"
          @click="closeNotification"
        />
      </div>
    </div>
  </transition>
</template>
